import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Nullable, TicketingFormCategory, Utils } from "@simplyk/common";
import { UseFormSetValue } from "react-hook-form";
import { v4 } from "uuid";

import { useLocaleField } from "../helpers/useLocaleField";

import { useRequestedOccurrenceId } from "./useRequestedOccurrenceId";

import { TicketingFormCategoryFeatures } from "@/common/constants/TicketingFormCategory";
import { hasNoDate } from "@/common/helpers/ticketing";
import { trpc } from "@/common/helpers/trpc";
import { useTranslate } from "@/common/hooks/useTranslate";
import { MembershipRenewalBuyerInfo, TicketingPaymentInput } from "@/common/types/ticketing";
import {
  DiscountWithRateMappings,
  OccurrenceWithRatesOutput,
  QuestionOutput,
  RateOutput,
  TicketingFrontendPageOutput,
  TicketingPreviewOutput,
} from "@/common/types/trpc";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { getEvent } from "@/components/PaymentProcessor/helper";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { Event } from "@/components/PaymentProcessor/type";

export interface PreviewTicketingData {
  occurrencesWithRates: OccurrenceWithRatesOutput[];
  selectedOccurrenceWithRates: OccurrenceWithRatesOutput | null;
  focusedPrizeId?: string | null;
}

export interface TicketingFormState {
  isTicketingClosed: boolean;
  returnToSelectionOnFullTicketing: (setValue: UseFormSetValue<TicketingPaymentInput>) => void;
  returnToSelectionOnNoTicketSelected: (setValue: UseFormSetValue<TicketingPaymentInput>) => void;
  hasDiscountCodes: boolean;
  validDiscount: DiscountWithRateMappings | null;
  setValidDiscount: (discount: DiscountWithRateMappings | null) => void;
  questions: QuestionOutput[];
  areQuestionsReady: boolean;
  areOccurrencesReady: boolean;
  selectedOccurrenceId: Nullable<string>;
  setSelectedOccurrenceId: (occurrenceId: string | null) => void;
  selectedOccurrenceWithRates: OccurrenceWithRatesOutput | undefined;
  occurrencesWithRates: OccurrenceWithRatesOutput[];
  hasADate: boolean;
  isCampaignRegistration: boolean;
  ticketingEvent: Event | undefined;
  membershipRenewalBuyerInfo: MembershipRenewalBuyerInfo | undefined;
  setMembershipRenewalBuyerInfo: (membershipRenewalBuyerInfo: MembershipRenewalBuyerInfo | undefined) => void;
  // The live editor selects the prize the admin is editing.
  previewFocusedPrizeId: string | null;
}

const buildPlaceholderOccurrenceRates = (ticketing: TicketingFrontendPageOutput): OccurrenceWithRatesOutput["rates"] =>
  (ticketing.rates ?? []).map((rate) => ({
    rateId: rate.id,
    remainingTickets: ticketing.formCategory === TicketingFormCategory.Auction ? undefined : rate.maximumToBuy,
    soldTicketsCount: 0,
  }));

// In preview, back-fill a placeholder rate (date set but no rate yet) immutably — the ticketing object
// is frozen by immer in the live editor, so an in-place write throws a TypeError.
export const useTicketingWithPreviewPlaceholderRates = (
  ticketing: TicketingFrontendPageOutput,
  isPreview: boolean
): TicketingFrontendPageOutput => {
  const { t } = useTranslate();

  return useMemo(() => {
    if (!isPreview || ticketing.rates?.length) {
      return ticketing;
    }
    const placeholderRates = [
      {
        amount: 2500,
        id: v4(),
        maximumToBuy: 10,
        photoUrls: [TicketingFormCategory.Custom, TicketingFormCategory.Shop, TicketingFormCategory.Auction].includes(
          ticketing.formCategory
        )
          ? ["/images/previewPlaceholders/item.png"]
          : null,
        rateFields: [
          {
            id: v4(),
            locale: ticketing.locale,
            title: t("ticketing", `${TicketingFormCategoryFeatures[ticketing.formCategory].defaultRateName}` as never),
            description: t("ticketing", "ticketingForm.preview.rateDescription"),
          },
        ],
        sortIndex: 0,
      },
    ] as RateOutput[];
    return { ...ticketing, rates: placeholderRates };
  }, [isPreview, ticketing, t]);
};

export const useTicketingFormState = ({
  ticketing,
  previewTicketingData,
  isPreview,
}: {
  ticketing: TicketingFrontendPageOutput & Pick<TicketingPreviewOutput, "discounts">;
  previewTicketingData?: PreviewTicketingData;
  isPreview: boolean;
}): TicketingFormState => {
  const isLiveEditorPreview = Boolean(previewTicketingData);
  const getLocaleField = useLocaleField();

  const { data: activeTicketingOccurrences, isLoading: isOccurrencesWithRatesLoading } =
    trpc.form_getActiveTicketingOccurrences.useQuery({ ticketingId: ticketing.id }, { enabled: !isLiveEditorPreview });

  const rawOccurrencesWithRates = previewTicketingData?.occurrencesWithRates ?? activeTicketingOccurrences;

  // In preview, back-fill the rates of empty occurrences (date set but no rate yet) immutably — the cached
  // occurrence objects are frozen by immer in the live editor, so an in-place write throws a TypeError.
  const occurrencesWithRates = useMemo(() => {
    if (!isPreview || !rawOccurrencesWithRates || !ticketing.rates?.length) {
      return rawOccurrencesWithRates;
    }
    const placeholderRates = buildPlaceholderOccurrenceRates(ticketing);
    return rawOccurrencesWithRates.map((occurrenceWithRates) =>
      occurrenceWithRates.rates.length === 0 ? { ...occurrenceWithRates, rates: placeholderRates } : occurrenceWithRates
    );
  }, [isPreview, rawOccurrencesWithRates, ticketing]);

  const hasOnlyOneDate = occurrencesWithRates?.length === 1 && Boolean(occurrencesWithRates[0].occurrence);
  const hasADate = !hasNoDate(occurrencesWithRates);

  const occurrenceWithRatesMapById = Utils.arrayToKeyMap(
    occurrencesWithRates || [],
    (occurrenceWithRates) => (occurrenceWithRates.occurrence?.id || null) as string
  );

  const defaultSelectedOccurrenceId = isLiveEditorPreview
    ? (previewTicketingData?.selectedOccurrenceWithRates?.occurrence?.id ?? null)
    : hasOnlyOneDate
      ? occurrencesWithRates?.[0]?.occurrence?.id || null
      : null;

  const [selectedOccurrenceId, setSelectedOccurrenceId] = useState<string | null>(defaultSelectedOccurrenceId);

  useEffect(() => {
    if (defaultSelectedOccurrenceId) {
      setSelectedOccurrenceId(defaultSelectedOccurrenceId);
    }
  }, [defaultSelectedOccurrenceId]);

  const requestedOccurrenceId = useRequestedOccurrenceId();
  const requestedAvailableOccurrenceId = isLiveEditorPreview
    ? null
    : (occurrenceWithRatesMapById[requestedOccurrenceId || "null"]?.occurrence?.id ?? null);
  const hasAppliedRequestedOccurrenceRef = useRef(false);

  // Applies the URL date once per mount, so a later data update never replaces the date the donor picked.
  useEffect(() => {
    if (!requestedAvailableOccurrenceId || hasAppliedRequestedOccurrenceRef.current) {
      return;
    }
    hasAppliedRequestedOccurrenceRef.current = true;
    setSelectedOccurrenceId((currentOccurrenceId) => currentOccurrenceId ?? requestedAvailableOccurrenceId);
  }, [requestedAvailableOccurrenceId]);

  const selectedOccurrenceWithRates = isLiveEditorPreview
    ? (occurrenceWithRatesMapById[previewTicketingData?.selectedOccurrenceWithRates?.occurrence?.id || "null"] ??
      previewTicketingData?.selectedOccurrenceWithRates ??
      undefined)
    : occurrenceWithRatesMapById[selectedOccurrenceId || "null"] || undefined;

  const { data: hasRemainingDiscounts } = trpc.form_getHasRemainingDiscountsByTicketingId.useQuery(
    { ticketingId: ticketing.id, occurrenceId: selectedOccurrenceId },
    { enabled: !isLiveEditorPreview }
  );

  const { data: ticketingQuestionsData } = trpc.form_getTicketingQuestions.useQuery(
    { ticketingId: ticketing.id, occurrenceId: selectedOccurrenceId },
    { enabled: !isLiveEditorPreview }
  );

  const selectedOccurrence = selectedOccurrenceWithRates?.occurrence;

  const [validDiscount, setValidDiscount] = useState<DiscountWithRateMappings | null>(null);

  const isMembershipV2 = ticketing.formCategory === TicketingFormCategory.MembershipV2;

  const returnToSelection = useCallback(
    (setValue: UseFormSetValue<TicketingPaymentInput>) => {
      window.scrollTo(0, 0);
      if (isMembershipV2 && ticketing.rates) {
        new Array<number>(ticketing.rates.length).fill(0).forEach((_, idx) => {
          setValue(`ticketsPurchased.${idx}.purchasedNumber`, 0);
        });
      }
    },
    [isMembershipV2, ticketing.rates]
  );

  const [membershipRenewalBuyerInfo, setMembershipRenewalBuyerInfo] = useState<MembershipRenewalBuyerInfo | undefined>(
    undefined
  );

  const questions = isLiveEditorPreview ? ticketing.questions || [] : ticketingQuestionsData || [];
  const areQuestionsReady = isLiveEditorPreview || ticketingQuestionsData !== undefined;

  const localeField = getLocaleField(ticketing.ticketingFields);

  const ticketingEvent = useMemo(() => {
    if (isLiveEditorPreview || !localeField) {
      return undefined;
    }
    return getEvent({ ticketing, ticketingField: localeField, selectedOccurrence });
  }, [isLiveEditorPreview, localeField, ticketing, selectedOccurrence]);

  const isTicketingClosed =
    !isLiveEditorPreview &&
    Boolean(
      ticketing.isArchived ||
      ticketing.closed ||
      selectedOccurrence?.closed ||
      (!isOccurrencesWithRatesLoading && activeTicketingOccurrences?.length === 0)
    );

  return {
    isTicketingClosed,
    returnToSelectionOnFullTicketing: returnToSelection,
    returnToSelectionOnNoTicketSelected: returnToSelection,
    hasDiscountCodes: isLiveEditorPreview ? Boolean(ticketing.discounts?.length) : Boolean(hasRemainingDiscounts),
    validDiscount,
    setValidDiscount,
    questions,
    areQuestionsReady,
    areOccurrencesReady: isLiveEditorPreview || activeTicketingOccurrences !== undefined,
    selectedOccurrenceId,
    setSelectedOccurrenceId,
    selectedOccurrenceWithRates,
    occurrencesWithRates: isLiveEditorPreview
      ? (occurrencesWithRates ?? [])
      : Object.values(occurrenceWithRatesMapById),
    hasADate,
    isCampaignRegistration: !isLiveEditorPreview && Boolean(ticketing.donationForm?.campaignId),
    ticketingEvent,
    membershipRenewalBuyerInfo,
    setMembershipRenewalBuyerInfo,
    previewFocusedPrizeId: previewTicketingData?.focusedPrizeId ?? null,
  };
};
